/*
 * @Author: liuhang liuhang@nextmar.com
 * @Date: 2025-07-23 17:00:00
 * @Description: Try-on API service - migrated from Vue project
 */

import { env } from '@/utils/env';

// 试戴接口使用当前站点后端 API 域名
const TRY_ON_URL = env.NEXT_PUBLIC_ZEELOOL_SERVER_API_BASE_URL;

export interface TryOnApiResponse<T = any> {
  code: number;
  data: T;
  message?: string;
}

export interface TryOnSkuData {
  sku: string;
  [key: string]: any;
}

export interface UploadImageData {
  upload_img: File;
  site_id: string;
  customer_id?: string;
  customer_email?: string;
}

export interface UploadUrlImageData {
  imageUrl: string;
  [key: string]: any;
}

export interface CustomerImageData {
  customerId?: string;
  [key: string]: any;
}

export interface SaveGuestImageData {
  image: File | Blob;
  [key: string]: any;
}

class TryOnApi {
  /**
   * 根据SKU获取试戴数据
   */
  async tryOn(data: TryOnSkuData): Promise<TryOnApiResponse> {
    return this.post(`${TRY_ON_URL}/api/tryon/get_sku_info`, data);
  }

  /**
   * 上传试戴图片
   */
  async uploadTryOnImg(data: UploadImageData): Promise<TryOnApiResponse> {
    const formData = new FormData();

    // 添加文件
    formData.append('upload_img', data.upload_img);

    // 添加其他字段
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'upload_img') {
        formData.append(key, String(value));
      }
    });

    return this.post(`${TRY_ON_URL}/api/tryon/photo_upload_form`, formData);
  }

  /**
   * 试戴上传图片并获取定位信息（通过URL）
   */
  async uploadUrlTryOnImg(data: UploadUrlImageData): Promise<TryOnApiResponse> {
    return this.post(`${TRY_ON_URL}/api/tryon/photo_upload_imageurl`, data);
  }

  /**
   * 删除试戴列表
   */
  async delTryOnList(data: CustomerImageData): Promise<TryOnApiResponse> {
    return this.get(`${TRY_ON_URL}/api/tryon/delete_customer_image`, data);
  }

  /**
   * 获取客户试戴图片
   */
  async getTryOn(data: CustomerImageData): Promise<TryOnApiResponse> {
    return this.get(`${TRY_ON_URL}/api/tryon/get_customer_image`, data);
  }

  /**
   * 保存访客试戴图片
   */
  async saveTryOn(data: SaveGuestImageData): Promise<TryOnApiResponse> {
    const formData = new FormData();

    // 添加图片
    formData.append('image', data.image);

    // 添加其他字段
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'image') {
        formData.append(key, String(value));
      }
    });

    return this.post(`${TRY_ON_URL}/api/tryon/save_guest_image`, formData);
  }

  private async request<T>(url: string, options: RequestInit = {}): Promise<TryOnApiResponse<T>> {
    const defaultHeaders: Record<string, string> = {};

    // 只在非FormData时设置Content-Type
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result as TryOnApiResponse<T>;
  }

  private async get<T>(
    endpoint: string,
    params?: Record<string, any>,
  ): Promise<TryOnApiResponse<T>> {
    const isRelativeEndpoint = endpoint.startsWith('/');
    // The base is only for parsing; keep same-origin requests relative when calling fetch.
    const url = new URL(endpoint, 'http://localhost');
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    return this.request<T>(isRelativeEndpoint ? `${url.pathname}${url.search}` : url.toString(), {
      headers: {
        'Content-Type': 'application/json, text/plain, */*',
      },
    });
  }

  private async post<T>(
    endpoint: string,
    data?: any,
    contentType?: string,
  ): Promise<TryOnApiResponse<T>> {
    const isFormData = data instanceof FormData;
    const headers: Record<string, string> = {};

    if (!isFormData) {
      headers['Content-Type'] = contentType || 'application/x-www-form-urlencoded';
    }

    let body: string | FormData;
    if (isFormData) {
      body = data;
    } else if (contentType === 'application/json') {
      body = JSON.stringify(data);
    } else {
      // URL encode the data
      body = new URLSearchParams(data).toString();
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      headers,
      body,
    });
  }
}

// 导出单例实例
export const tryOnApi = new TryOnApi();
